import { useMemo, useState } from "react"
import { CircleAlertIcon, PlusIcon, Trash2Icon } from "lucide-react"

import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { getTermError, type SearchTerm, type SummarizerLabel } from "@/utils/summarizer-config"
import { useDebouncedField } from "./useDebouncedField"

interface SearchTermsPanelProps {
  label: SummarizerLabel
  canWrite: boolean
  canDelete: boolean
  onChange: (updater: (label: SummarizerLabel) => SummarizerLabel) => void
}

export function SearchTermsPanel({ label, canWrite, canDelete, onChange }: SearchTermsPanelProps) {
  const terms = label.rawSearchTerms

  function addTerm(term: SearchTerm) {
    onChange((current) => ({ ...current, rawSearchTerms: [...current.rawSearchTerms, term] }))
  }

  function patchTerm(index: number, patch: Partial<SearchTerm>) {
    onChange((current) => ({
      ...current,
      rawSearchTerms: current.rawSearchTerms.map((t, i) => (i === index ? { ...t, ...patch } : t)),
    }))
  }

  function deleteTerm(index: number) {
    onChange((current) => ({ ...current, rawSearchTerms: current.rawSearchTerms.filter((_, i) => i !== index) }))
  }

  return (
    <div className="flex flex-col gap-4">
      {canWrite && <AddTermForm onAdd={addTerm} />}

      {terms.length === 0 ? (
        <p className="py-6 text-center text-sm text-muted-foreground">No search terms yet</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {terms.map((searchTerm, index) => (
            <SearchTermRow
              key={searchTerm.id}
              searchTerm={searchTerm}
              index={index}
              canWrite={canWrite}
              canDelete={canDelete}
              onPatch={(patch) => patchTerm(index, patch)}
              onDelete={() => deleteTerm(index)}
            />
          ))}
        </ul>
      )}
    </div>
  )
}

function SearchTermRow({
  searchTerm,
  index,
  canWrite,
  canDelete,
  onPatch,
  onDelete,
}: {
  searchTerm: SearchTerm
  index: number
  canWrite: boolean
  canDelete: boolean
  onPatch: (patch: Partial<SearchTerm>) => void
  onDelete: () => void
}) {
  const term = useDebouncedField(
    searchTerm.term,
    (value) => onPatch({ term: value }),
    300,
    (value) => value.trim(),
  )
  const comment = useDebouncedField(
    searchTerm.comment ?? "",
    (value) => {
      onPatch({ comment: value.length > 0 ? value : undefined })
    },
    300,
    (value) => value.trim(),
  )
  const error = useMemo(
    () => getTermError({ term: term.value.trim(), isRegex: searchTerm.isRegex }),
    [searchTerm.isRegex, term.value],
  )

  function setRegex(checked: boolean) {
    term.flush()
    comment.flush()
    onPatch({ isRegex: checked })
  }

  return (
    <li className="grid grid-cols-1 items-start gap-3 rounded-md border p-3 sm:grid-cols-[minmax(0,1fr)_auto] lg:grid-cols-[minmax(0,1fr)_auto_16rem_auto]">
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <Input
          value={term.value}
          onChange={(e) => term.onChange(e.target.value)}
          onBlur={term.flush}
          readOnly={!canWrite}
          placeholder="Search term"
          aria-label={`Search term ${index + 1}`}
          aria-invalid={error != null}
        />
        {error != null && (
          <p
            role="alert"
            className="flex items-center gap-1 text-sm text-destructive max-lg:items-start max-lg:[overflow-wrap:anywhere]"
          >
            <CircleAlertIcon className="size-4 shrink-0" /> {error}
          </p>
        )}
      </div>

      <div className="flex h-9 items-center gap-2">
        <Checkbox
          id={`term-regex-${searchTerm.id}`}
          checked={searchTerm.isRegex}
          disabled={!canWrite}
          onCheckedChange={(checked) => setRegex(checked === true)}
        />
        <label htmlFor={`term-regex-${searchTerm.id}`} className="cursor-pointer text-sm">
          Regex
        </label>
      </div>

      <Input
        value={comment.value}
        onChange={(e) => comment.onChange(e.target.value)}
        onBlur={comment.flush}
        readOnly={!canWrite}
        placeholder="Comment (optional)"
        aria-label={`Comment for search term ${index + 1}`}
        className="w-full lg:w-64"
      />

      {canDelete && (
        <Button
          type="button"
          size="icon"
          variant="ghost"
          aria-label={`Delete search term ${index + 1}`}
          className="text-red-600 hover:text-red-700"
          onClick={onDelete}
        >
          <Trash2Icon />
        </Button>
      )}
    </li>
  )
}

function AddTermForm({ onAdd }: { onAdd: (term: SearchTerm) => void }) {
  const [term, setTerm] = useState("")
  const [isRegex, setIsRegex] = useState(false)
  const [hasSubmitted, setHasSubmitted] = useState(false)

  const candidate = { term: term.trim(), isRegex }
  const error = term === "" && !hasSubmitted ? null : getTermError(candidate)

  function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setHasSubmitted(true)

    if (getTermError(candidate) != null) return

    const form = e.currentTarget
    const commentValue = new FormData(form).get("comment")
    const comment = typeof commentValue === "string" ? commentValue.trim() : ""

    onAdd({
      id: crypto.randomUUID(),
      ...candidate,
      ...(comment.length === 0 ? {} : { comment }),
    })
    form.reset()
    setTerm("")
    setIsRegex(false)
    setHasSubmitted(false)
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-2 rounded-md border bg-muted/30 p-3">
      <div className="flex flex-col gap-1">
        <Input
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder="Enter search term"
          aria-label="New search term"
          aria-invalid={error != null}
          className="bg-background"
        />
        {error != null && (
          <p
            role="alert"
            className="flex items-center gap-1 text-sm text-destructive max-lg:items-start max-lg:[overflow-wrap:anywhere]"
          >
            <CircleAlertIcon className="size-4 shrink-0" /> {error}
          </p>
        )}
      </div>
      <Input
        name="comment"
        placeholder="Comment (optional)"
        aria-label="New search term comment"
        className="bg-background"
      />
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <Checkbox id="new-term-regex" checked={isRegex} onCheckedChange={(checked) => setIsRegex(checked === true)} />
          <label htmlFor="new-term-regex" className="cursor-pointer text-sm">
            Regex
          </label>
        </div>
        <Button type="submit" size="sm">
          <PlusIcon /> Add search term
        </Button>
      </div>
    </form>
  )
}
